import { Capacitor, registerPlugin } from '@capacitor/core';

const STORAGE_KEY = 'mt_passenger_play_review_v1';
const MAX_AUTOMATIC_ATTEMPTS = 2;
const MIN_ATTEMPT_GAP_MS = 60 * 24 * 60 * 60 * 1000;
// A queued ride is only worth acting on close to when it finished. Past this the
// "you just hit a milestone" moment is gone — drop it so a permanently-stuck
// backend count can never keep a pending record (or its retries) alive forever.
const MAX_PENDING_AGE_MS = 3 * 24 * 60 * 60 * 1000;
const MILESTONES = new Set([3, 10]);
const PlayReview = registerPlugin('PlayReview');

const isAndroidNative = () => {
  try { return Capacitor.getPlatform?.() === 'android' && Capacitor.isNativePlatform?.(); } catch { return false; }
};

const readState = (uid) => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return value?.uid === uid
      ? { uid, attempts: Array.isArray(value.attempts) ? value.attempts : [], pending: value.pending || null }
      : { uid, attempts: [], pending: null };
  } catch {
    return { uid, attempts: [], pending: null };
  }
};

const writeState = (state) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* storage unavailable */ }
};

/**
 * Called only after a completed trip's rating sheet is closed. This records no
 * sentiment and does not ask a user anything; it merely carries the completed
 * trip to the idle Book screen where an eligible Play request may happen.
 */
export const queuePlayReviewForCompletedRide = ({ uid, rideId, targetCompletedRides = null }) => {
  if (!uid || !rideId) return;
  const state = readState(uid);
  state.pending = {
    rideId: String(rideId),
    queuedAt: Date.now(),
    targetCompletedRides: Number.isFinite(Number(targetCompletedRides))
      ? Math.floor(Number(targetCompletedRides))
      : null,
  };
  writeState(state);
};

/** Avoid a lifetime-stat callable when no completed ride is awaiting review. */
export const hasQueuedPlayReview = (uid) => Boolean(uid && readState(uid).pending);

/** Returns whether the Book screen should retry after a delayed profile aggregate. */
export const requestQueuedPlayReview = async ({ uid, completedRides }) => {
  if (!uid) return { state: 'none' };
  const state = readState(uid);
  const pending = state.pending;
  if (!pending) return { state: 'none' };

  const queuedAt = Number(pending.queuedAt);
  if (!Number.isFinite(queuedAt) || Date.now() - queuedAt > MAX_PENDING_AGE_MS) {
    state.pending = null;
    writeState(state);
    return { state: 'stale' };
  }

  const count = Math.max(0, Math.floor(Number(completedRides) || 0));
  const target = Number(pending.targetCompletedRides);
  // The user profile aggregation can arrive just after the completed-ride
  // screen. Wait for the known milestone instead of dropping a valid prompt.
  if (Number.isFinite(target) && MILESTONES.has(target) && count < target) {
    return { state: 'waiting' };
  }

  // A completed ride is consumed once evaluated. This prevents any automatic
  // retry loop after quota suppression or a no-Play-Store test install.
  state.pending = null;
  if (!isAndroidNative()) {
    writeState(state);
    return { state: 'not_native' };
  }

  const milestone = Number.isFinite(target) && MILESTONES.has(target) ? target : count;
  if (!MILESTONES.has(milestone)) {
    writeState(state);
    return { state: 'not_milestone' };
  }

  const now = Date.now();
  const lastAttemptAt = Math.max(0, ...state.attempts.map((attempt) => Number(attempt?.at) || 0));
  if (state.attempts.length >= MAX_AUTOMATIC_ATTEMPTS || (lastAttemptAt && now - lastAttemptAt < MIN_ATTEMPT_GAP_MS)) {
    writeState(state);
    return { state: 'throttled' };
  }

  // Record the attempt before opening Play. The platform may silently decline
  // to show its UI and gives us no signal, so we must never hammer users.
  state.attempts.push({ at: now, milestone, rideId: pending.rideId });
  writeState(state);
  try {
    await PlayReview.requestReview();
  } catch (err) {
    console.warn('Play review request unavailable:', err?.message || err);
  }
  return { state: 'attempted' };
};

/** Profile action only — never called from an automatic review trigger. */
export const openPlayStoreListing = async () => {
  if (isAndroidNative()) {
    try {
      await PlayReview.openStoreListing();
      return true;
    } catch (err) {
      console.warn('Play Store link unavailable:', err?.message || err);
    }
  }
  if (typeof window !== 'undefined') {
    window.open('https://play.google.com/store/apps/details?id=in.mytownly.rides', '_blank', 'noopener,noreferrer');
  }
  return false;
};
